import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LocalPizzaRoundedIcon from "@mui/icons-material/LocalPizzaRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import DeliveryDiningRoundedIcon from "@mui/icons-material/DeliveryDiningRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";

import ApiServices from "../Utils/ApiServices";

const REDGRAD =
  "linear-gradient(135deg, #7A0E0E 0%, #B71C1C 50%, #E64A19 100%)";
const AMBERGRAD =
  "linear-gradient(135deg, #FF8F00 0%, #FFB300 50%, #FFC107 100%)";

const COUNTRY_CODES = [
  { code: "US", label: "United States", dial_code: "+1" },
  { code: "IN", label: "India", dial_code: "+91" },
  { code: "GB", label: "United Kingdom", dial_code: "+44" },
  { code: "CA", label: "Canada", dial_code: "+1" },
];

const PERKS = [
  { icon: <LocalFireDepartmentRoundedIcon />, text: "Wood-fired in 90 seconds" },
  { icon: <DeliveryDiningRoundedIcon />, text: "Free delivery over $25" },
  { icon: <StarRoundedIcon />, text: "Earn rewards on every order" },
];

const SignUpPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("md"));

  const [formData, setFormData] = useState({
    client_id: process.env.REACT_APP_CLIENT_ID,
    country_code: "+91",
    phone: "",
    first_name: "",
    last_name: "",
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const selectedCountry = useMemo(
    () =>
      COUNTRY_CODES.find((i) => i.dial_code === formData.country_code) ||
      COUNTRY_CODES[0],
    [formData.country_code]
  );

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });
  const handleCloseSnackbar = () =>
    setSnackbar((p) => ({ ...p, open: false }));

  const validateForm = () => {
    const e = {};
    if (!formData.first_name.trim()) e.first_name = "First name is required";
    if (!formData.last_name.trim()) e.last_name = "Last name is required";
    if (!formData.phone.trim()) e.phone = "Phone number is required";
    if (!formData.email.trim()) e.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      e.email = "Enter a valid email";
    if (!formData.password.trim()) e.password = "Password is required";
    else if (formData.password.length < 6)
      e.password = "Minimum 6 characters required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
    setErrors((p) => ({ ...p, [field]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showSnackbar("Please fill all required fields", "error");
      return;
    }
    try {
      setLoading(true);
      const payload = {
        client_id: formData.client_id,
        phone: `${formData.country_code}${formData.phone}`,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        password: formData.password,
      };
      await ApiServices.post("/api/v1/customers/register/", payload);
      showSnackbar("Welcome aboard! Redirecting to login...", "success");
      setTimeout(() => navigate("/login"), 1500);
    } catch (error) {
      const msg =
        error?.response?.data?.detail ||
        "Registration failed. Please try again.";
      showSnackbar(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 56,
      borderRadius: "14px",
      backgroundColor: "#FFF8F0",
      transition: "all .25s ease",
      "& fieldset": { borderColor: "rgba(122,14,14,0.15)" },
      "&:hover fieldset": { borderColor: "#E64A19" },
      "&.Mui-focused": {
        backgroundColor: "#fff",
        boxShadow: "0 0 0 4px rgba(255,179,0,0.18)",
      },
      "&.Mui-focused fieldset": { borderColor: "#B71C1C", borderWidth: 2 },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#B71C1C" },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(900px 500px at 10% 0%, rgba(255,179,0,0.18), transparent 60%), radial-gradient(900px 500px at 100% 100%, rgba(230,74,25,0.18), transparent 60%), #FFF8F0",
        display: "flex",
        alignItems: "center",
        py: { xs: 3, md: 6 },
        px: { xs: 2, md: 3 },
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <Card
            elevation={0}
            sx={{
              borderRadius: { xs: 3, md: 5 },
              overflow: "hidden",
              boxShadow:
                "0 30px 80px rgba(122,14,14,0.18), 0 8px 24px rgba(0,0,0,0.06)",
              border: "1px solid rgba(255,255,255,0.6)",
              backdropFilter: "blur(20px)",
              backgroundColor: "rgba(255,255,255,0.85)",
            }}
          >
            <Grid container>
              {/* Brand Panel */}
              <Grid
                item
                xs={12}
                md={5}
                sx={{
                  background: REDGRAD,
                  color: "#fff",
                  p: { xs: 4, md: 5 },
                  position: "relative",
                  overflow: "hidden",
                  minHeight: { md: 640 },
                }}
              >
                {/* floating blobs */}
                <Box
                  component={motion.div}
                  animate={{ y: [0, -14, 0], rotate: [0, 8, 0] }}
                  transition={{ duration: 7, repeat: Infinity }}
                  sx={{
                    position: "absolute",
                    top: -40,
                    right: -40,
                    width: 200,
                    height: 200,
                    borderRadius: "50%",
                    background:
                      "radial-gradient(circle, rgba(255,179,0,0.45), transparent 70%)",
                  }}
                />
                <Box
                  component={motion.div}
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 6, repeat: Infinity }}
                  sx={{
                    position: "absolute",
                    bottom: -60,
                    left: -40,
                    width: 240,
                    height: 240,
                    borderRadius: "50%",
                    background:
                      "radial-gradient(circle, rgba(255,255,255,0.18), transparent 70%)",
                  }}
                />

                <Stack
                  spacing={3}
                  sx={{ position: "relative", zIndex: 1, height: "100%" }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: "16px",
                        background: AMBERGRAD,
                        display: "grid",
                        placeItems: "center",
                        boxShadow: "0 10px 24px rgba(255,143,0,0.45)",
                      }}
                    >
                      <LocalPizzaRoundedIcon sx={{ color: "#7A0E0E" }} />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: "'Playfair Display', serif",
                          fontWeight: 800,
                          fontSize: 24,
                          lineHeight: 1,
                        }}
                      >
                        Pizza Town
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11,
                          letterSpacing: 2,
                          opacity: 0.85,
                          textTransform: "uppercase",
                        }}
                      >
                        Authentic • Fresh • Fast
                      </Typography>
                    </Box>
                  </Stack>

                  <Box sx={{ mt: { xs: 2, md: 6 } }}>
                    <Typography
                      sx={{
                        fontFamily: "'Playfair Display', serif",
                        fontWeight: 800,
                        fontSize: { xs: 32, md: 42 },
                        lineHeight: 1.1,
                      }}
                    >
                      Join the table.
                    </Typography>
                    <Typography
                      sx={{
                        mt: 1.5,
                        fontSize: { xs: 14, md: 16 },
                        opacity: 0.92,
                        maxWidth: 360,
                      }}
                    >
                      Create your account and unlock faster checkout, saved
                      addresses, and rewards on every slice.
                    </Typography>
                  </Box>

                  <Stack spacing={1.5} sx={{ mt: { xs: 2, md: 4 } }}>
                    {PERKS.map((p, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 + i * 0.1 }}
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                          sx={{
                            background: "rgba(255,255,255,0.12)",
                            backdropFilter: "blur(10px)",
                            border: "1px solid rgba(255,255,255,0.18)",
                            borderRadius: 2,
                            px: 2,
                            py: 1.25,
                          }}
                        >
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: "10px",
                              background: "rgba(255,179,0,0.25)",
                              display: "grid",
                              placeItems: "center",
                              color: "#FFD54F",
                            }}
                          >
                            {p.icon}
                          </Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                            {p.text}
                          </Typography>
                        </Stack>
                      </motion.div>
                    ))}
                  </Stack>
                </Stack>
              </Grid>

              {/* Form Panel */}
              <Grid item xs={12} md={7}>
                <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      sx={{
                        fontFamily: "'Playfair Display', serif",
                        fontWeight: 800,
                        fontSize: { xs: 26, md: 32 },
                        color: "#2A1810",
                      }}
                    >
                      Create your account
                    </Typography>
                    <Typography sx={{ color: "#6b4a3a", mt: 0.5, fontSize: 14 }}>
                      It only takes a minute. Your next pizza is waiting.
                    </Typography>
                  </Box>

                  <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="First Name"
                          fullWidth
                          value={formData.first_name}
                          onChange={(e) =>
                            handleInputChange("first_name", e.target.value)
                          }
                          error={Boolean(errors.first_name)}
                          helperText={errors.first_name}
                          sx={inputSx}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonOutlineRoundedIcon
                                  sx={{ color: "#B71C1C" }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Last Name"
                          fullWidth
                          value={formData.last_name}
                          onChange={(e) =>
                            handleInputChange("last_name", e.target.value)
                          }
                          error={Boolean(errors.last_name)}
                          helperText={errors.last_name}
                          sx={inputSx}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonOutlineRoundedIcon
                                  sx={{ color: "#B71C1C" }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={5}>
                        <Autocomplete
                          options={COUNTRY_CODES}
                          value={selectedCountry}
                          disableClearable
                          onChange={(_, v) =>
                            handleInputChange(
                              "country_code",
                              v?.dial_code || "+1"
                            )
                          }
                          getOptionLabel={(o) => `${o.label} (${o.dial_code})`}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Country"
                              sx={inputSx}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={7}>
                        <TextField
                          label="Phone Number"
                          fullWidth
                          value={formData.phone}
                          onChange={(e) =>
                            handleInputChange("phone", e.target.value)
                          }
                          error={Boolean(errors.phone)}
                          helperText={errors.phone}
                          sx={inputSx}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIphoneRoundedIcon
                                  sx={{ color: "#B71C1C" }}
                                />
                                <Typography
                                  sx={{
                                    ml: 1,
                                    color: "#2A1810",
                                    fontWeight: 600,
                                  }}
                                >
                                  {formData.country_code}
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Email"
                          type="email"
                          fullWidth
                          value={formData.email}
                          onChange={(e) =>
                            handleInputChange("email", e.target.value)
                          }
                          error={Boolean(errors.email)}
                          helperText={errors.email}
                          sx={inputSx}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailOutlinedIcon sx={{ color: "#B71C1C" }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Password"
                          type={showPwd ? "text" : "password"}
                          fullWidth
                          value={formData.password}
                          onChange={(e) =>
                            handleInputChange("password", e.target.value)
                          }
                          error={Boolean(errors.password)}
                          helperText={
                            errors.password || "Use at least 6 characters"
                          }
                          sx={inputSx}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockOutlinedIcon sx={{ color: "#B71C1C" }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPwd((s) => !s)}
                                  edge="end"
                                  sx={{ color: "#6b4a3a" }}
                                >
                                  {showPwd ? (
                                    <VisibilityOffRoundedIcon />
                                  ) : (
                                    <VisibilityRoundedIcon />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          fullWidth
                          disabled={loading}
                          component={motion.button}
                          whileHover={{ y: -2 }}
                          whileTap={{ scale: 0.98 }}
                          sx={{
                            mt: 1,
                            minHeight: 56,
                            borderRadius: "14px",
                            fontSize: "1rem",
                            fontWeight: 700,
                            textTransform: "none",
                            color: "#fff",
                            background: REDGRAD,
                            backgroundSize: "200% 200%",
                            boxShadow:
                              "0 14px 30px rgba(183,28,28,0.35), 0 2px 8px rgba(0,0,0,0.08)",
                            "&:hover": {
                              boxShadow:
                                "0 18px 40px rgba(183,28,28,0.5), 0 4px 12px rgba(0,0,0,0.1)",
                              backgroundPosition: "100% 0",
                            },
                            "&.Mui-disabled": {
                              color: "rgba(255,255,255,0.8)",
                              opacity: 0.7,
                            },
                          }}
                        >
                          {loading ? (
                            <CircularProgress size={24} sx={{ color: "#fff" }} />
                          ) : (
                            "Create Account"
                          )}
                        </Button>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          textAlign="center"
                          sx={{ fontSize: 14, color: "#6b4a3a", mt: 1 }}
                        >
                          Already have an account?{" "}
                          <Box
                            component="span"
                            onClick={() => navigate("/login")}
                            sx={{
                              color: "#B71C1C",
                              fontWeight: 700,
                              cursor: "pointer",
                              borderBottom: "2px solid transparent",
                              transition: "border-color .2s",
                              "&:hover": { borderColor: "#FFB300" },
                            }}
                          >
                            Sign In
                          </Box>
                        </Typography>

                        <Typography
                          textAlign="center"
                          sx={{
                            fontSize: 12,
                            color: "#9b7a6a",
                            mt: 2,
                          }}
                        >
                          By signing up you agree to our{" "}
                          <Box component="span" sx={{ color: "#B71C1C", fontWeight: 600 }}>
                            Terms
                          </Box>{" "}
                          &{" "}
                          <Box component="span" sx={{ color: "#B71C1C", fontWeight: 600 }}>
                            Privacy Policy
                          </Box>
                          .
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Grid>
            </Grid>
          </Card>
        </motion.div>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={handleCloseSnackbar}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SignUpPage;
